import React, { Fragment, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import { Row, Col, Container, Button, Card, Alert } from "react-bootstrap";
import { AxisDate, AxisDateTimeFull } from "../util/AxisDateTime";
import AxisSpinner from "./AxisSpinner";
import { PaginationControl } from "react-bootstrap-pagination-control";
import TimesheetWeekViewerModal from "../modals/TimesheetWeekViewerModal";
import { SuccessCheckMark } from "./AxisCheckMark";
import { BsCalendarWeek, BsArrowRepeat } from "react-icons/bs";

const TimesheetWeekTable = () => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [fetchCount, setFetchCount] = useState(25);
  const [sortKey, setSortKey] = useState("weekEnding");
  const [sortDirection, setSortDirection] = useState("dsc");
  const [showTimesheetWeek, setShowTimesheetWeek] = useState(false);
  const [selectedTimesheetWeek, setSelectedTimesheetWeek] = useState(null);

  useEffect(() => {
    paginate();
  }, [sortKey, sortDirection, offset]);

  const paginate = async (e) => {
    try {
      setLoading(true);

      const pageSettings = {
        direction: sortDirection,
        key: sortKey,
        page: offset,
        elementPerPage: fetchCount,
      };
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {},
      };

      const page = await authAxios().post(
        "/api/backoffice/reporting/timesheet_week_paging",
        pageSettings,
        config,
      );
      setLoading(false);

      if (page.data.content.length === 0) {
        if (dataSource.length === 0) {
          setTotalRows(0);
        }
        console.log("empty api call");
        setDataSource([]);
        return;
      }

      setDataSource(page.data.content);
      setTotalRows(page.data.totalElements);
      var length = Math.ceil(page.data.totalElements / fetchCount);
      setTotalPages(length);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
      setDataSource([]);
      setTotalRows(0);
    }
  };

  const handleSetSortKey = (key) => {
    if (sortKey === key) {
      if (sortDirection === "asc") {
        setSortDirection("dsc");
      } else {
        setSortDirection("asc");
      }
      return;
    }
    setSortKey(key);
  };

  const tableHeading = (key, name) => {
    if (key === sortKey) {
      return sortDirection === "dsc" ? name + " ↓" : name + " ↑";
    }
    return name;
  };

  const SpinnerOrData = (loading) => {
    if (loading) return (
      <div className="text-center py-5">
        <AxisSpinner loading={loading} />
      </div>
    );

    if (!dataSource || dataSource.length === 0) {
      return (
        <Alert variant="info">
          No timesheet weeks available.
        </Alert>
      );
    }

    return (
      <Fragment>
        <div className="table-responsive">
          <Table striped bordered hover className="mb-0">
            <thead className="bg-light">
              <tr style={{ cursor: "pointer" }}>
                <th onClick={() => handleSetSortKey("weekEnding")} style={{position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1}}>
                  {tableHeading("weekEnding", "Week Ending")}
                </th>
                <th onClick={() => handleSetSortKey("isCurrent")} style={{position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1}}>
                  {tableHeading("isCurrent", "Current")}
                </th>
                <th onClick={() => handleSetSortKey("created")} style={{position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1}}>
                  {tableHeading("created", "Created")}
                </th>
                <th style={{position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1}}>Last Modified</th>
              </tr>
            </thead>
            <tbody>
              {dataSource.map((timesheetWeek) => (
                <tr
                  key={timesheetWeek?.id}
                  onClick={() => {
                    setSelectedTimesheetWeek(timesheetWeek);
                    setShowTimesheetWeek(!showTimesheetWeek);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td>{AxisDate(timesheetWeek?.weekEnding)}</td>
                  <td>
                    {timesheetWeek?.current === true ? SuccessCheckMark() : null}
                  </td>
                  <td>{AxisDateTimeFull(timesheetWeek?.created)}</td>
                  <td>{AxisDateTimeFull(timesheetWeek?.lastModification)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="mt-3">
          <div className="text-center mb-2">
            <PaginationControl
              page={offset + 1}
              between={3}
              total={totalRows}
              limit={fetchCount}
              changePage={(page) => {
                setOffset(page - 1);
              }}
              ellipsis={1}
            />
          </div>
          <div className="text-center">
            <small className="text-muted">
              Page {offset + 1} of {totalPages}, showing {dataSource?.length}{" "}
              records(s) out of {totalRows}
            </small>
          </div>
        </div>
      </Fragment>
    );
  };

  return (
    <Container fluid>
      <Card className="mb-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">
                <BsCalendarWeek className="me-2" />
                Timesheet Weeks
              </h5>
            </Col>
            <Col xs="auto">
              <Button variant="outline-primary" size="sm" onClick={() => paginate()}>
                <BsArrowRepeat className="me-1" /> Refresh
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {SpinnerOrData(loading)}
        </Card.Body>
      </Card>
      
      <TimesheetWeekViewerModal
        visible={showTimesheetWeek}
        close={() => setShowTimesheetWeek(false)}
        timesheetWeek={selectedTimesheetWeek}
      />
    </Container>
  );
};

export default TimesheetWeekTable;
